import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../hooks/useNotification';
import {
  LoadingSpinner,
  Modal,
} from '../design-system';
import { FaGithub } from 'react-icons/fa';
import apiClient from '../services/unifiedApiClient';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

const GitHubIntegration = () => {
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionType, setConnectionType] = useState(null); // 'app' or 'pat'
  const [userData, setUserData] = useState(null);
  const [installations, setInstallations] = useState([]);
  const [showPATModal, setShowPATModal] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [disconnectingId, setDisconnectingId] = useState(null);
  const { isAuthenticated } = useAuth();
  const { showNotification } = useNotification();

  const checkConnection = async () => {
    if (!isAuthenticated()) return;

    setLoading(true);
    try {
      const data = await apiClient.get('/github/status');
      if (data) {
        setIsConnected(data.connected || false);
        setConnectionType(data.connection_type || null);
        setUserData(data.user || null);
        setInstallations(data.installations || []);
      } else {
        setIsConnected(false);
        setConnectionType(null);
        setUserData(null);
        setInstallations([]);
      }
    } catch (error) {
      console.error('Failed to check GitHub connection:', error);
      setIsConnected(false);
      setUserData(null);
      setInstallations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkConnection();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Install GitHub App (preferred flow) — opens in new tab, polls for completion
  const installGitHubApp = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/github/app/install-url');
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener');
        // Poll for new installation — browsers throttle timers in background tabs,
        // so we also listen for visibility changes to check immediately when the
        // user switches back after completing the install in the new tab.
        const prevCount = installations.length;

        const checkForInstall = async () => {
          try {
            const status = await apiClient.get('/github/status');
            if (status?.connected && (status.installations || []).length > prevCount) {
              return status;
            }
          } catch {
            // ignore polling errors
          }
          return null;
        };

        const handleDetected = (status) => {
          setIsConnected(true);
          setConnectionType(status.connection_type || 'app');
          setUserData(status.user || null);
          setInstallations(status.installations || []);
          setLoading(false);
        };

        const pollInterval = setInterval(async () => {
          const status = await checkForInstall();
          if (status) {
            clearInterval(pollInterval);
            document.removeEventListener('visibilitychange', onVisible);
            handleDetected(status);
          }
        }, 3000);

        const onVisible = async () => {
          if (document.visibilityState === 'visible') {
            const status = await checkForInstall();
            if (status) {
              clearInterval(pollInterval);
              document.removeEventListener('visibilitychange', onVisible);
              handleDetected(status);
            }
          }
        };
        document.addEventListener('visibilitychange', onVisible);

        // Stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          document.removeEventListener('visibilitychange', onVisible);
          setLoading(false);
        }, 5 * 60 * 1000);
        return;
      } else {
        setShowPATModal(true);
        setLoading(false);
      }
    } catch (error) {
      console.warn('GitHub App not available, falling back to PAT:', error);
      setShowPATModal(true);
      setLoading(false);
    }
  };

  // Connect via PAT (fallback)
  const connectWithPAT = async () => {
    if (!accessToken) {
      showNotification({ type: 'error', message: 'Please enter your GitHub access token' });
      return;
    }

    setLoading(true);
    try {
      const data = await apiClient.post('/github/connect', { access_token: accessToken });

      showNotification({
        type: 'success',
        message: `Connected to GitHub as ${data.username || 'your account'}`
      });

      setIsConnected(true);
      setConnectionType('pat');
      setUserData(data.user || null);
      setAccessToken('');
      setShowPATModal(false);
      await checkConnection();
    } catch (error) {
      showNotification({
        type: 'error',
        message: error.response?.data?.detail || error.userMessage || error.message || 'Failed to connect to GitHub'
      });
    } finally {
      setLoading(false);
    }
  };

  // Disconnect a specific App installation
  const disconnectInstallation = async (installationId) => {
    const inst = installations.find(i => i.id === installationId);
    if (!window.confirm(`Disconnect GitHub ${inst?.account_type === 'Organization' ? 'organization' : 'account'} "${inst?.account_login}"?`)) return;

    setDisconnectingId(installationId);
    try {
      await apiClient.delete(`/github/disconnect?installation_id=${installationId}`);
      showNotification({ type: 'success', message: `Disconnected ${inst?.account_login}` });

      const uninstallUrl = `https://github.com/settings/installations/${installationId}`;
      if (window.confirm('To fully revoke access, you can also uninstall the app on GitHub. Open GitHub settings?')) {
        window.open(uninstallUrl, '_blank', 'noopener');
      }

      await checkConnection();
    } catch (error) {
      showNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to disconnect'
      });
    } finally {
      setDisconnectingId(null);
    }
  };

  // Disconnect PAT
  const disconnectPAT = async () => {
    if (!window.confirm('Are you sure you want to disconnect your GitHub account?')) return;

    setLoading(true);
    try {
      await apiClient.delete('/github/disconnect');
      showNotification({ type: 'success', message: 'GitHub account disconnected' });
      setIsConnected(false);
      setConnectionType(null);
      setUserData(null);
      setInstallations([]);
    } catch (error) {
      showNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to disconnect GitHub account'
      });
    } finally {
      setLoading(false);
    }
  };

  const getManageUrl = (inst) => {
    if (inst.account_type === 'Organization') {
      return `https://github.com/organizations/${inst.account_login}/settings/installations/${inst.id}`;
    }
    return `https://github.com/settings/installations/${inst.id}`;
  };

  return (
    <div style={{
      backgroundColor: T.card,
      border: `1px solid ${T.rule}`,
      borderRadius: T.r4,
      boxShadow: T.sh1,
      padding: '24px',
      fontFamily: T.sans,
    }}>
      {/* Header row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '20px',
      }}>
        <div>
          <div style={{
            fontSize: '17px',
            fontWeight: 700,
            color: T.ink,
            marginBottom: '4px',
            fontFamily: T.sans,
          }}>
            GitHub Integration
          </div>
          <div style={{
            fontSize: '13px',
            color: T.inkMuted,
            fontFamily: T.sans,
          }}>
            Connect your GitHub account to provide repository context during task creation
          </div>
        </div>

        <button
          type="button"
          onClick={installGitHubApp}
          disabled={!isAuthenticated() || loading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            backgroundColor: isConnected && connectionType === 'app' ? 'transparent' : T.orange,
            color: isConnected && connectionType === 'app' ? T.ink2 : '#fff',
            border: isConnected && connectionType === 'app' ? `1px solid ${T.rule}` : '1px solid transparent',
            borderRadius: T.r3,
            padding: '7px 14px',
            fontSize: '13px',
            fontWeight: 600,
            fontFamily: T.sans,
            cursor: (!isAuthenticated() || loading) ? 'not-allowed' : 'pointer',
            opacity: (!isAuthenticated() || loading) ? 0.55 : 1,
            transition: 'background 0.15s, opacity 0.15s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => {
            if (!(!isAuthenticated() || loading)) {
              e.currentTarget.style.backgroundColor = isConnected && connectionType === 'app' ? T.paperDeep : T.orangeDark;
            }
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = isConnected && connectionType === 'app' ? 'transparent' : T.orange;
          }}
        >
          <FaGithub size={15} />
          {isConnected && connectionType === 'app' ? 'Connect Another Account' : 'Install GitHub App'}
        </button>
      </div>

      {/* Auth warning */}
      {!isAuthenticated() && (
        <div style={{
          backgroundColor: '#FFF8E6',
          border: `1px solid ${T.ruleStrong}`,
          borderRadius: T.r3,
          padding: '12px 16px',
          marginBottom: '16px',
        }}>
          <div style={{ fontWeight: 600, color: T.ink2, fontSize: '14px', marginBottom: '2px', fontFamily: T.sans }}>
            Authentication Required
          </div>
          <div style={{ color: T.inkMuted, fontSize: '13px', fontFamily: T.sans }}>
            Please log in to connect your GitHub account
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && !isConnected ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <LoadingSpinner size="lg" />
          <div style={{ marginTop: '12px', color: T.inkMuted, fontSize: '13px', fontFamily: T.sans }}>
            Checking connection...
          </div>
        </div>

      ) : connectionType === 'app' && installations.length > 0 ? (
        /* App installations list */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {installations.map((inst) => (
            <div
              key={inst.id}
              style={{
                backgroundColor: '#F3F8EE',
                border: `1px solid #B8D4A0`,
                borderRadius: T.r3,
                padding: '14px 16px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Connected checkmark */}
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                    <circle cx="10" cy="10" r="10" fill={T.moss} opacity="0.15" />
                    <path d="M6 10.5l2.5 2.5 5-5" stroke={T.moss} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: 600, color: T.ink, fontSize: '14px', fontFamily: T.sans }}>
                      {inst.account_login}
                      {inst.account_type === 'Organization' && (
                        <span style={{ fontWeight: 400, color: T.inkFaint, marginLeft: '8px', fontSize: '12px' }}>
                          (Organization)
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: T.inkMuted, marginTop: '2px', fontFamily: T.sans }}>
                      {inst.repository_selection === 'all' ? 'All repositories' : 'Selected repositories'}
                      {' · '}
                      <a
                        href={getManageUrl(inst)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: T.orange, textDecoration: 'none' }}
                      >
                        Manage repository access
                      </a>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => disconnectInstallation(inst.id)}
                  disabled={disconnectingId === inst.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    backgroundColor: 'transparent',
                    color: disconnectingId === inst.id ? T.inkFaint : T.capRed,
                    border: `1px solid ${disconnectingId === inst.id ? T.rule : '#E0A89A'}`,
                    borderRadius: T.r2,
                    padding: '5px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    fontFamily: T.sans,
                    cursor: disconnectingId === inst.id ? 'not-allowed' : 'pointer',
                    opacity: disconnectingId === inst.id ? 0.6 : 1,
                    transition: 'opacity 0.15s',
                  }}
                >
                  {disconnectingId === inst.id ? <LoadingSpinner size="sm" /> : 'Disconnect'}
                </button>
              </div>
            </div>
          ))}
        </div>

      ) : isConnected && connectionType === 'pat' && userData ? (
        /* PAT connected state */
        <div style={{
          backgroundColor: '#F3F8EE',
          border: `1px solid #B8D4A0`,
          borderRadius: T.r3,
          padding: '14px 16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" style={{ flexShrink: 0 }}>
                <circle cx="11" cy="11" r="11" fill={T.moss} opacity="0.15" />
                <path d="M7 11.5l2.5 2.5 5.5-5.5" stroke={T.moss} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <div style={{ fontWeight: 600, color: T.ink, fontSize: '14px', fontFamily: T.sans }}>
                  Connected via Personal Access Token
                </div>
                <div style={{ fontSize: '13px', color: T.inkMuted, marginTop: '2px', fontFamily: T.sans }}>
                  {userData.login || userData.username}
                  {userData.public_repos && ` — ${userData.public_repos} public repositories`}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={disconnectPAT}
              disabled={loading}
              style={{
                backgroundColor: 'transparent',
                color: T.capRed,
                border: `1px solid #E0A89A`,
                borderRadius: T.r2,
                padding: '5px 12px',
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: T.sans,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                transition: 'opacity 0.15s',
              }}
            >
              Disconnect
            </button>
          </div>
        </div>

      ) : !isConnected ? (
        /* Not connected state */
        <div style={{
          backgroundColor: T.paperSoft,
          border: `1px solid ${T.rule}`,
          borderRadius: T.r3,
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
            <circle cx="11" cy="11" r="11" fill={T.inkFaint} opacity="0.18" />
            <path d="M11 7v5" stroke={T.inkMuted} strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="11" cy="15.5" r="1" fill={T.inkMuted} />
          </svg>
          <div>
            <div style={{ fontWeight: 600, color: T.ink2, fontSize: '14px', fontFamily: T.sans, marginBottom: '2px' }}>
              Not Connected
            </div>
            <div style={{ fontSize: '13px', color: T.inkMuted, fontFamily: T.sans }}>
              Install the GitHub App to grant access to specific repositories for drift detection and context analysis
            </div>
          </div>
        </div>
      ) : null}

      {/* PAT Fallback Modal */}
      <Modal
        isOpen={showPATModal}
        onClose={() => setShowPATModal(false)}
        title="Connect with Personal Access Token"
      >
        <div style={{ marginBottom: '20px' }}>
          {/* Info notice */}
          <div style={{
            backgroundColor: '#F0F4FF',
            border: '1px solid #C5D0F5',
            borderRadius: T.r3,
            padding: '12px 14px',
            marginBottom: '18px',
          }}>
            <p style={{ margin: 0, fontSize: '13px', color: T.ink2, fontFamily: T.sans }}>
              The GitHub App is not yet configured. You can connect with a Personal Access Token instead.
            </p>
          </div>

          <label style={{
            display: 'block',
            fontWeight: 600,
            fontSize: '13px',
            color: T.ink2,
            marginBottom: '6px',
            fontFamily: T.sans,
          }}>
            Personal Access Token
          </label>
          <input
            type="password"
            placeholder="ghp_..."
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              border: `1px solid ${T.rule}`,
              borderRadius: T.r2,
              backgroundColor: T.card,
              color: T.ink,
              fontSize: '14px',
              fontFamily: T.mono,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <p style={{
            margin: '6px 0 0',
            fontSize: '12px',
            color: T.inkMuted,
            fontFamily: T.sans,
          }}>
            Create a{' '}
            <a
              href="https://github.com/settings/tokens/new?scopes=repo,read:user"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: T.orange, textDecoration: 'none' }}
            >
              personal access token
            </a>{' '}
            with 'repo' and 'read:user' scopes.
          </p>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
        }}>
          <button
            type="button"
            onClick={() => setShowPATModal(false)}
            style={{
              backgroundColor: 'transparent',
              color: T.ink2,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r3,
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: T.sans,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={connectWithPAT}
            disabled={loading || !accessToken}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              backgroundColor: T.orange,
              color: '#fff',
              border: '1px solid transparent',
              borderRadius: T.r3,
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: T.sans,
              cursor: (loading || !accessToken) ? 'not-allowed' : 'pointer',
              opacity: (loading || !accessToken) ? 0.55 : 1,
              transition: 'background 0.15s, opacity 0.15s',
            }}
            onMouseEnter={e => {
              if (!(loading || !accessToken)) e.currentTarget.style.backgroundColor = T.orangeDark;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = T.orange;
            }}
          >
            {loading ? <LoadingSpinner size="sm" /> : <FaGithub size={14} />}
            Connect
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default GitHubIntegration;
